import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Mail, CheckCircle2, AlertTriangle, Loader2, Plus, Send, Star, Trash2, Pencil, Copy, Check, Globe } from "lucide-react";

const STATUS_META = {
  verified: { label: "Verified", cls: "bg-[#E7EFE6] text-[#4F6A4C]", Icon: CheckCircle2 },
  pending: { label: "Verification pending", cls: "bg-[#FBF0DA] text-[#8A6D2F]", Icon: Loader2 },
  temporary_failure: { label: "Verification pending", cls: "bg-[#FBF0DA] text-[#8A6D2F]", Icon: Loader2 },
  partially_verified: { label: "Partly verified", cls: "bg-[#FBF0DA] text-[#8A6D2F]", Icon: Loader2 },
  partially_failed: { label: "Some records failed", cls: "bg-[#F6E2DE] text-[#8A3B2F]", Icon: AlertTriangle },
  not_started: { label: "DNS required", cls: "bg-[#FBF0DA] text-[#8A6D2F]", Icon: AlertTriangle },
  failed: { label: "Error", cls: "bg-[#F6E2DE] text-[#8A3B2F]", Icon: AlertTriangle },
  failure: { label: "Error", cls: "bg-[#F6E2DE] text-[#8A3B2F]", Icon: AlertTriangle },
  none: { label: "Not set up", cls: "bg-[#F0EBE1] text-[#5C5856]", Icon: Globe },
};

const SPIN_STATUSES = ["pending", "temporary_failure", "partially_verified"];

function RecordStatus({ status }) {
  const s = (status || "").toLowerCase();
  if (s === "verified") return <span className="ml-auto inline-flex items-center gap-1 text-[#4F6A4C] font-medium"><CheckCircle2 className="w-3.5 h-3.5" /> Verified</span>;
  if (s === "failed" || s === "failure") return <span className="ml-auto inline-flex items-center gap-1 text-[#8A3B2F] font-medium"><AlertTriangle className="w-3.5 h-3.5" /> Not detected</span>;
  if (s === "pending" || s === "not_started" || s === "temporary_failure") return <span className="ml-auto inline-flex items-center gap-1 text-[#8A6D2F]"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Pending</span>;
  return <span className="ml-auto text-[#8A857F]">{status || ""}</span>;
}

function CopyBtn({ text, testid }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button" data-testid={testid}
      onClick={() => { navigator.clipboard?.writeText(text || ""); setDone(true); setTimeout(() => setDone(false), 1400); }}
      className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-[#E5E2DC] hover:border-[#2A2626] text-[#5C5856] transition-colors shrink-0"
    >
      {done ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
    </button>
  );
}

export function EmailSettings() {
  const [loading, setLoading] = useState(true);
  const [senders, setSenders] = useState([]);
  const [domain, setDomain] = useState({ domain: "", domain_status: "none", domain_records: [] });
  const [domainInput, setDomainInput] = useState("");
  const [busy, setBusy] = useState(false);

  const [editor, setEditor] = useState(null);
  const [testFor, setTestFor] = useState(null);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const domainVerified = domain.domain_status === "verified" && domain.domain;
  const replyDomains = senders.map((s) => (s.reply_to_email || "").split("@")[1]).filter(Boolean);
  const guessRoot = (domainVerified && domain.domain.replace(/^email\./, "")) || replyDomains[0] || "yourchurch.org";
  const sendingDomain = domainVerified ? domain.domain : `email.${guessRoot}`;
  const platformFrom = domain.platform_from_address || "";

  const load = useCallback(async () => {
    try {
      const [{ data: s }, { data: d }] = await Promise.all([
        api.get("/tenant/senders"),
        api.get("/tenant/email-settings"),
      ]);
      setSenders(s || []);
      setDomain(d || {});
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => setEditor({ internal_name: "", display_name: "", from_local_part: "office", reply_to_email: "", _new: true });
  const saveEditor = async () => {
    if (!editor.internal_name.trim()) { toast.error("Add a sender name"); return; }
    setBusy(true);
    try {
      const body = {
        internal_name: editor.internal_name, display_name: editor.display_name || editor.internal_name,
        from_local_part: editor.from_local_part, reply_to_email: editor.reply_to_email,
      };
      if (editor._new) await api.post("/tenant/senders", body);
      else await api.put(`/tenant/senders/${editor.profile_id}`, body);
      toast.success("Sender saved");
      setEditor(null); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save sender"); }
    finally { setBusy(false); }
  };
  const setDefault = async (p) => {
    try { await api.post(`/tenant/senders/${p.profile_id}/default`); await load(); toast.success(`${p.internal_name} is now your default sender`); }
    catch { toast.error("Could not set default"); }
  };
  const doDelete = async () => {
    const p = confirmDelete; setConfirmDelete(null); setBusy(true);
    try { await api.delete(`/tenant/senders/${p.profile_id}`); await load(); toast.success("Sender deleted"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete sender"); }
    finally { setBusy(false); }
  };
  const sendTest = async () => {
    if (!testTo.trim()) { toast.error("Enter a recipient email"); return; }
    setTesting(true);
    try {
      const { data } = await api.post(`/tenant/senders/${testFor.profile_id}/test`, { to: testTo.trim() });
      toast.success(`Test sent from ${data.from_address}`);
      setTestFor(null); setTestTo("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send test email"); }
    finally { setTesting(false); }
  };

  const connectDomain = async () => {
    setBusy(true);
    try { const { data } = await api.post("/tenant/email-domain", { domain: domainInput.trim() }); setDomain(data); setDomainInput(""); toast.success("Domain added — add the DNS records below, then verify"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not add domain"); }
    finally { setBusy(false); }
  };
  const verifyDomain = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/tenant/email-domain/verify"); setDomain(data);
      if (data.domain_status === "verified") { toast.success(data.domain_message || "Your church domain is verified!"); await load(); }
      else toast.message("Domain not verified yet", { description: data.domain_message || "DNS changes can take a while to appear. You can check again shortly." });
    } catch (e) { toast.error(e?.response?.data?.detail || "We couldn't check your domain right now. Please try again."); }
    finally { setBusy(false); }
  };
  const disconnectDomain = async () => {
    setBusy(true);
    try { await api.delete("/tenant/email-domain"); await load(); toast.success("Custom domain disconnected"); }
    catch { toast.error("Could not disconnect"); }
    finally { setBusy(false); }
  };

  const meta = STATUS_META[domain.domain_status] || STATUS_META.none;
  const hasDomain = domain.domain_status && domain.domain_status !== "none";

  return (
    <div className="space-y-6 max-w-3xl" data-testid="email-settings-panel">
      <div>
        <h2 className="text-lg font-semibold text-[#2A2626]">Email</h2>
        <p className="text-sm text-[#5C5856]">Manage who MinistryHub sends email on behalf of and connect your church's sending domain.</p>
      </div>

      {/* ============ EMAIL SENDERS ============ */}
      <Card className="p-6" data-testid="email-senders-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-semibold text-[#2A2626] flex items-center gap-2"><Mail className="w-4 h-4" /> Email senders</h3>
            <p className="text-sm text-[#5C5856] mt-1">Create and manage the people and ministries MinistryHub can send email on behalf of.</p>
          </div>
          <Button onClick={openNew} className="rounded-full bg-[#2A2626] hover:bg-[#1A1717] text-white" data-testid="sender-add"><Plus className="w-4 h-4 mr-1" /> Add sender</Button>
        </div>

        {loading ? (
          <div className="mt-5 text-sm text-[#5C5856] inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <div className="mt-4 space-y-3">
            {senders.map((p) => (
              <div key={p.profile_id} className="rounded-xl border border-[#E5E2DC] p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`sender-row-${p.profile_id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-[#2A2626]">{p.internal_name}</span>
                    {p.is_default && <Badge className="bg-[#2A2626] text-white text-[10px]" data-testid="sender-default-badge">Default</Badge>}
                  </div>
                  <p className="text-sm text-[#5C5856] mt-0.5">{p.display_name}</p>
                  <p className="text-[12px] text-[#8A857F] mt-1">{p.from_address}{p.reply_to_email ? ` · Replies to ${p.reply_to_email}` : ""}</p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0 flex-wrap">
                  <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => setEditor({ ...p })} data-testid={`sender-edit-${p.profile_id}`}><Pencil className="w-3.5 h-3.5 mr-1" /> Edit</Button>
                  <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => { setTestFor(p); setTestTo(""); }} data-testid={`sender-test-${p.profile_id}`}><Send className="w-3.5 h-3.5 mr-1" /> Send test</Button>
                  {!p.is_default && <Button size="sm" variant="ghost" className="rounded-full h-8" onClick={() => setDefault(p)} data-testid={`sender-default-${p.profile_id}`}><Star className="w-3.5 h-3.5 mr-1" /> Default</Button>}
                  {senders.length > 1 && <Button size="sm" variant="ghost" className="rounded-full h-8 text-[#8A3B2F]" onClick={() => setConfirmDelete(p)} data-testid={`sender-delete-${p.profile_id}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* ============ SENDING DOMAIN ============ */}
      <Card className="p-6" data-testid="sending-domain-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-semibold text-[#2A2626] flex items-center gap-2"><Globe className="w-4 h-4" /> Send from your church domain</h3>
            <p className="text-sm text-[#5C5856] mt-1 max-w-xl">
              Optional. Make emails look like they came directly from your church. MinistryHub can send using a subdomain of your church website, such as <code className="bg-[#F0EBE1] px-1 rounded">{sendingDomain}</code>. This doesn't change your website or your existing church email accounts — MinistryHub email already works without it.
            </p>
          </div>
          <span className={`text-xs px-2.5 py-1 rounded-full font-medium inline-flex items-center gap-1.5 ${meta.cls}`} data-testid="email-domain-status">
            <meta.Icon className={`w-3.5 h-3.5 ${SPIN_STATUSES.includes(domain.domain_status) ? "animate-spin" : ""}`} /> {meta.label}
          </span>
        </div>

        {loading ? null : !hasDomain ? (
          <div className="mt-4">
            <Label className="text-xs">Choose a sending subdomain</Label>
            <div className="flex gap-2 flex-wrap mt-1">
              <Input value={domainInput} onChange={(e) => setDomainInput(e.target.value)} placeholder={sendingDomain} className="h-10 flex-1 min-w-[240px]" data-testid="email-domain-input" />
              <Button onClick={connectDomain} disabled={busy || !domainInput.trim()} className="rounded-full bg-[#2A2626] hover:bg-[#1A1717] text-white h-10 px-5" data-testid="email-domain-connect">{busy ? "Adding…" : "Continue"}</Button>
            </div>
            <p className="text-[11px] text-[#8A857F] mt-2">We recommend a dedicated subdomain like <strong>{sendingDomain}</strong> — not your main website address.</p>
          </div>
        ) : (
          <>
            <div className="mt-4 rounded-lg bg-[#F7F5F1] border border-[#E5E2DC] p-4">
              <p className="text-sm text-[#2A2626]">Sending domain: <strong>{domain.domain}</strong></p>
              {domainVerified ? (
                <p className="text-[13px] text-[#4F6A4C] mt-1 inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Your church domain is connected. Your senders now send from <strong>{domain.domain}</strong>.</p>
              ) : (
                <p className="text-[12px] text-[#5C5856] mt-1">Add these records wherever your church's domain is managed (usually Cloudflare, GoDaddy, Squarespace, Wix, or Namecheap). Not sure? Ask whoever manages your church's website. Changes can take a few minutes to several hours.</p>
              )}
            </div>

            {!domainVerified && (
              <div className="mt-3 space-y-2" data-testid="email-dns-records">
                {(domain.domain_records || []).map((r, i) => (
                  <div key={i} className="rounded-lg border border-[#EEE] p-3 text-[12px]" data-testid={`email-dns-record-${i}`}>
                    <div className="flex items-center gap-2 mb-1"><span className="font-semibold text-[#2A2626]">{r.type}</span>{r.priority != null && <span className="text-[#8A857F]">priority {r.priority}</span>}<RecordStatus status={r.status} /></div>
                    <div className="flex items-center gap-2 mt-1"><span className="text-[#8A857F] w-14 shrink-0">Host</span><span className="break-all text-[#444] flex-1">{r.name}</span><CopyBtn text={r.name} testid={`dns-copy-host-${i}`} /></div>
                    <div className="flex items-center gap-2 mt-1"><span className="text-[#8A857F] w-14 shrink-0">Value</span><span className="break-all text-[#444] flex-1">{r.value}</span><CopyBtn text={r.value} testid={`dns-copy-value-${i}`} /></div>
                  </div>
                ))}
                {(domain.domain_records || []).length === 0 && <p className="text-[12px] text-[#8A857F]">No DNS records yet — click "Check again".</p>}
                <div className="rounded-lg bg-[#FCF6E9] border border-[#EAD9AE] p-3 text-[12px] text-[#7A6A44] mt-2" data-testid="wix-dns-warning">
                  <p className="font-medium text-[#8A6D2F]">Using Wix for DNS?</p>
                  <p className="mt-1">Wix currently limits custom MX records for subdomains, which may prevent custom sending-domain verification. If you can't add the MX record above, MinistryHub email will still work using its verified sending address — you can skip the custom domain.</p>
                  <p className="mt-2"><strong>Having trouble adding these records?</strong> Whoever manages your church's website can add them, or you may need a DNS provider that supports custom MX records for subdomains. Don't remove or replace your existing Google Workspace or Microsoft 365 email records.</p>
                </div>
              </div>
            )}

            <div className="mt-4 flex gap-2 flex-wrap items-center">
              {!domainVerified && (
                <Button onClick={verifyDomain} disabled={busy} className="rounded-full bg-[#2A2626] hover:bg-[#1A1717] text-white" data-testid="email-domain-verify">
                  {busy ? "Checking…" : (domain.domain_status === "not_started" ? "I've added the DNS records — Verify domain" : "Check again")}
                </Button>
              )}
              <Button onClick={disconnectDomain} disabled={busy} variant="ghost" className="rounded-full text-[#8A857F]" data-testid="email-domain-disconnect">Disconnect</Button>
            </div>
            {!domainVerified && domain.domain_status !== "not_started" && (
              <p className="text-[12px] text-[#8A6D2F] mt-2">We're waiting for your DNS changes to appear. This often takes a few minutes, but some providers may take several hours. You don't need to keep this page open.</p>
            )}
          </>
        )}
      </Card>

      {/* ---- Sender editor dialog ---- */}
      <Dialog open={!!editor} onOpenChange={(o) => !o && setEditor(null)}>
        <DialogContent className="max-w-lg" data-testid="sender-editor">
          <DialogHeader><DialogTitle>{editor?._new ? "Add sender" : "Edit sender"}</DialogTitle></DialogHeader>
          {editor && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs">Sender name</Label>
                <Input value={editor.internal_name} onChange={(e) => setEditor({ ...editor, internal_name: e.target.value })} placeholder="e.g. Main Office, Women's Ministry" className="h-10 mt-1" data-testid="sender-internal-name" />
                <p className="text-[11px] text-[#8A857F] mt-1">An internal name your team will recognize.</p>
              </div>
              <div>
                <Label className="text-xs">Display name</Label>
                <Input value={editor.display_name} onChange={(e) => setEditor({ ...editor, display_name: e.target.value })} placeholder="e.g. Your Church Women's Ministry" className="h-10 mt-1" data-testid="sender-display-name" />
                <p className="text-[11px] text-[#8A857F] mt-1">The name guests will see in their inbox.</p>
              </div>
              <div>
                <Label className="text-xs">From email</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input value={editor.from_local_part} onChange={(e) => setEditor({ ...editor, from_local_part: e.target.value.replace(/[^a-z0-9._-]/gi, "").toLowerCase() })} className="h-10 w-40" data-testid="sender-local-part" />
                  <span className="text-sm text-[#5C5856]">@ {domainVerified ? domain.domain : sendingDomain}</span>
                </div>
                <p className="text-[11px] text-[#8A857F] mt-1">This becomes your sending address once your church domain is verified. {!domainVerified && `Until then, MinistryHub sends from ${platformFrom || "its verified address"} using your sender name and Reply-To.`}</p>
              </div>
              <div>
                <Label className="text-xs">Reply-To email</Label>
                <Input type="email" value={editor.reply_to_email} onChange={(e) => setEditor({ ...editor, reply_to_email: e.target.value })} placeholder={`office@${guessRoot}`} className="h-10 mt-1" data-testid="sender-reply-to" />
                <p className="text-[11px] text-[#8A857F] mt-1">When someone replies, their response goes here — normally a real church inbox.</p>
              </div>
              <div className="rounded-lg bg-[#F7F5F1] border border-[#E5E2DC] p-3 text-[12px] space-y-3" data-testid="sender-preview">
                {domainVerified ? (
                  <div>
                    <div className="text-[#8A857F] uppercase tracking-wide text-[10px] mb-1">Guests will see</div>
                    <div className="text-[#2A2626] font-semibold">{editor.display_name || editor.internal_name || "Your Church"}</div>
                    <div className="text-[#444]" data-testid="sender-preview-from">{(editor.from_local_part || "office")}@{domain.domain}</div>
                    <div className="text-[#5C5856] mt-1">Replies go to {editor.reply_to_email || `office@${guessRoot}`}</div>
                  </div>
                ) : (
                  <>
                    <div>
                      <div className="text-[#8A857F] uppercase tracking-wide text-[10px] mb-1">Guests will currently see</div>
                      <div className="text-[#2A2626] font-semibold">{editor.display_name || editor.internal_name || "Your Church"}</div>
                      <div className="text-[#444]" data-testid="sender-preview-from">{platformFrom || "your MinistryHub sending address"}</div>
                      <div className="text-[#5C5856] mt-1">Replies go to {editor.reply_to_email || `office@${guessRoot}`}</div>
                    </div>
                    <div className="pt-2 border-t border-[#E5E2DC]">
                      <div className="text-[#8A857F] uppercase tracking-wide text-[10px] mb-1">Once your church domain is verified</div>
                      <div className="text-[#2A2626] font-semibold">{editor.display_name || editor.internal_name || "Your Church"}</div>
                      <div className="text-[#444]" data-testid="sender-preview-future">{(editor.from_local_part || "office")}@{sendingDomain}</div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditor(null)}>Cancel</Button>
            <Button onClick={saveEditor} disabled={busy} className="bg-[#2A2626] hover:bg-[#1A1717] text-white" data-testid="sender-save">{busy ? "Saving…" : "Save sender"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- Test email dialog ---- */}
      <Dialog open={!!testFor} onOpenChange={(o) => !o && setTestFor(null)}>
        <DialogContent className="max-w-md" data-testid="test-email-dialog">
          <DialogHeader><DialogTitle>Send a test email</DialogTitle></DialogHeader>
          <p className="text-sm text-[#5C5856]">Send a real branded test from <strong>{testFor?.internal_name}</strong> so you can see exactly what guests receive.</p>
          <div className="mt-2">
            <Label className="text-xs">Send to</Label>
            <Input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@yourchurch.org" className="h-10 mt-1" data-testid="test-email-to" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTestFor(null)}>Cancel</Button>
            <Button onClick={sendTest} disabled={testing} className="bg-[#2A2626] hover:bg-[#1A1717] text-white" data-testid="test-email-send">{testing ? "Sending…" : "Send test"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- Delete confirm ---- */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="sender-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{confirmDelete?.internal_name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Any Connect Cards currently sending from this sender will switch back to your church default sender. Your other senders and Reply-To settings won't be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-[#8A3B2F] hover:bg-[#6f2f26]">Delete sender</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
